import React, { useEffect, useState, useCallback } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

import { useDispatch, useSelector } from "react-redux";
import { getEvents } from "../../App/Slices/eventsSlice";

const SixCardSlider = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const SearchParams = new URLSearchParams(window.location.search);
    const searchText = SearchParams.get('q');
    const dispatch = useDispatch();
    const { events } = useSelector((state) => state.eventsData);

    const [selectedImages, setSelectedImages] = useState([]);
    const [currentImage, setCurrentImage] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [imageError, setImageError] = useState(false);

    // Event cards slider settings
    const settings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 3,
        slidesToScroll: 1,
        rows: 1,
        autoplay: false,
        responsive: [
            {
                breakpoint: 768,
                settings: { slidesToShow: 1, rows: 1 },
            },
            {
                breakpoint: 1024,
                settings: { slidesToShow: 3, rows: 1 },
            },
        ],
    };

    useEffect(() => {
        dispatch(getEvents({ status: "active", searchText }));
    }, [dispatch, searchText]);

    // Open modal
    const handleCardClick = (galleryImages, mainImage) => {
        let images = [];
        try {
            images = JSON.parse(galleryImages) || [];
        } catch (err) {
            console.error("Invalid gallery_images JSON", err);
        }
        if (mainImage) images.unshift(mainImage);

        // Reset states
        setIsLoading(true);
        setImageError(false);
        setSelectedImages(images);
        setCurrentImage(0);
        setIsModalOpen(true);
        document.body.style.overflow = 'hidden'; // Prevent body scroll

        // Preload the first image
        const img = new Image();
        img.src = `${baseUrl}/${images[0]}`;
        img.onload = () => setIsLoading(false);
        img.onerror = () => {
            setIsLoading(false);
            setImageError(true);
        };
    };

    // Close modal
    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedImages([]);
        setCurrentImage(0);
        document.body.style.overflow = 'unset'; // Restore body scroll
    }, []);

    // Navigate to previous image
    const previousImage = useCallback(() => {
        setIsLoading(true);
        setImageError(false);
        const newIndex = currentImage === 0 ? selectedImages.length - 1 : currentImage - 1;

        // Preload the image before showing
        const img = new Image();
        img.src = `${baseUrl}/${selectedImages[newIndex]}`;
        img.onload = () => {
            setCurrentImage(newIndex);
            setIsLoading(false);
        };
        img.onerror = () => {
            setCurrentImage(newIndex);
            setIsLoading(false);
            setImageError(true);
        };
    }, [currentImage, selectedImages, baseUrl]);

    // Navigate to next image
    const nextImage = useCallback(() => {
        setIsLoading(true);
        setImageError(false);
        const newIndex = currentImage === selectedImages.length - 1 ? 0 : currentImage + 1;

        // Preload the image before showing
        const img = new Image();
        img.src = `${baseUrl}/${selectedImages[newIndex]}`;
        img.onload = () => {
            setCurrentImage(newIndex);
            setIsLoading(false);
        };
        img.onerror = () => {
            setCurrentImage(newIndex);
            setIsLoading(false);
            setImageError(true);
        };
    }, [currentImage, selectedImages, baseUrl]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isModalOpen) return;

            switch (e.key) {
                case 'Escape':
                    closeModal();
                    break;
                case 'ArrowLeft':
                    previousImage();
                    break;
                case 'ArrowRight':
                    nextImage();
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isModalOpen, selectedImages.length, closeModal, previousImage, nextImage]);

    return (
        <>
            <div className="container mt-5 mb-5 letest-articals-sections-blog events-section-card">
                {/* <h2 className="text-center">Latest Articles</h2> */}
                {events?.length > 2 ?
                    <Slider {...settings}>
                        {events?.map((article) => (
                            <div key={article?.id} className="card-container cursor-pointer" onClick={() =>
                                handleCardClick(article?.gallery_images, article?.image)
                            }>
                                <div className="card">
                                    <img src={`${baseUrl}/${article?.image}`} className="card-img-top" alt={article?.title} loading="lazy"
                                    />
                                    <div className="card-body text-center">
                                        <h5 className="card-title">{article?.title}</h5>
                                        <p className="card-text">{article?.description}</p>
                                        <button className="btn btn-primary mt-auto read-more-blogs-btn text-center">Read more</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </Slider> :
                    <div className="flex justify-center gap-6 flex-wrap">
                        {events?.map((article) => (
                            <div key={article?.id} className="card-container cursor-pointer" onClick={() =>
                                handleCardClick(article?.gallery_images, article?.image)
                            }>
                                <div className="card">
                                    <img src={`${baseUrl}/${article?.image}`} className="card-img-top" alt={article?.title} loading="lazy"
                                    />
                                    <div className="card-body text-center">
                                        <h5 className="card-title">{article?.title}</h5>
                                        <p className="card-text">{article?.description}</p>
                                        <button className="btn btn-primary mt-auto text-center">Read more</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                }
            </div>

            {/* Gallery Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999]">
                    <div className="relative w-full h-full flex items-center justify-center p-4">
                        {/* Close Button */}
                        <button
                            onClick={closeModal}
                            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors duration-200 z-[9999] bg-black bg-opacity-50 rounded-full p-2"
                            aria-label="Close modal"
                        >
                            <X size={32} />
                        </button>

                        {/* Previous Button */}
                        {selectedImages?.length > 1 && (
                            <button
                                onClick={previousImage}
                                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors duration-200 bg-black bg-opacity-50 rounded-full p-2 z-10"
                                aria-label="Previous image"
                            >
                                <ChevronLeft size={32} />
                            </button>
                        )}

                        {/* Image Container */}
                        <div className="relative max-w-7xl max-h-full flex items-center justify-center">
                            {selectedImages[currentImage] && (
                                <>
                                    <img
                                        key={currentImage}
                                        src={`${baseUrl}/${selectedImages[currentImage]}`}
                                        alt={`Gallery ${currentImage + 1}`}
                                        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl transition-opacity duration-300"
                                        style={{ maxHeight: 'calc(100vh - 120px)' }}
                                        onLoad={() => setIsLoading(false)}
                                        onError={() => {
                                            setIsLoading(false);
                                            setImageError(true);
                                        }}
                                    />
                                    {isLoading && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
                                        </div>
                                    )}
                                    {imageError && (
                                        <div className="text-white text-center">
                                            Failed to load image
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Image Counter */}
                            {selectedImages?.length > 1 && (
                                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 text-white px-4 py-2 rounded-full text-sm font-medium">
                                    {currentImage + 1} / {selectedImages?.length}
                                </div>
                            )}
                        </div>

                        {/* Next Button */}
                        {selectedImages?.length > 1 && (
                            <button
                                onClick={nextImage}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors duration-200 bg-black bg-opacity-50 rounded-full p-2 z-10"
                                aria-label="Next image"
                            >
                                <ChevronRight size={32} />
                            </button>
                        )}


                    </div>

                    {/* Click outside to close */}
                    <div
                        className="absolute inset-0 -z-10"
                        onClick={closeModal}
                        aria-label="Click to close modal"
                    />
                </div>
            )}
        </>
    );
};

export default SixCardSlider;